import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { WebPubSubService } from './services/WebPubSubService';
import { environment } from '../environments/environment';
import { NavigationEnd, Router, Event } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, filter, exhaustMap, takeUntil, EMPTY, first } from 'rxjs';
import { AuthService, GenericError } from '@auth0/auth0-angular';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Patient Engagement'; 
  metaData: any;
  patientID_npi: any;
  isSecurePatientComm!: boolean;
  deferredPrompt: any;
  showInstallButton = false;
  currentUrl: string = '';
  hasDeferredPromptFired: boolean = false;
  installMessage = '';
  installMessageForAndroid: boolean = false;
  installMessageForIos: boolean = false;
  private storageListener: any;
  private visibilityListener: any;
  private sessionCheckInterval: any;
  private destroy$ = new Subject<void>();
  private isLoggingOut = false;

  constructor(
    private webPubSubService: WebPubSubService,
    private _authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // --- Patient WebSocket (existing logic) ---
    this.isSecurePatientComm = environment.isSecurePatientComm;
    this.metaData = JSON.parse(localStorage.getItem('MetaData') as string);  
    if (this.metaData && this.isSecurePatientComm) {
      this.patientID_npi = JSON.stringify(this.metaData?.patientId) + '_' + this.metaData?.pharmacyNpi;
      this.webPubSubService.initializePatientWebSocket(this.patientID_npi)
        .catch(err => console.error('[AppComponent] WebSocket init failed (chat unavailable):', err));
    }

    // Cross-tab logout via storage event (works between browser tabs)
    this.storageListener = async (event: StorageEvent) => {
      if (event.key === 'logout-event') {
       await this.logoutAndRedirect();
      }
    };
    window.addEventListener('storage', this.storageListener);

    // Cross-context logout via visibility check (works between standalone PWA and browser).
    // The storage event doesn't fire between standalone PWA and browser on mobile,
    // so we check for logout-event in localStorage when the page becomes visible.
    this.visibilityListener = async () => {
      if (document.visibilityState === 'visible' && !this.isLoggingOut) {
        const logoutEvent = localStorage.getItem('logout-event');
        if (logoutEvent) {
          console.log('[AppComponent] Detected logout-event on visibility change → logging out');
          await this.logoutAndRedirect();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    // --- PWA Install prompt logic (existing logic, unchanged) ---
    window.addEventListener('beforeinstallprompt', (e: any) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.hasDeferredPromptFired = true;
    });

    const userAgent = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(userAgent);
    const dismissed = localStorage.getItem('installBannerDismissed');
    const isGuestUser = localStorage.getItem('IsGuestUser');

    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = event.url;
        const isInStandaloneMode =
          window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
        if (
          isIos &&
          this.currentUrl.includes('/dashboard') &&
          dismissed !== 'true' &&
          isGuestUser !== 'true'
        ) {
          if (isInStandaloneMode) {
            this.showInstallButton = false;
            return;
          }
          this.showInstallButton = true;
          this.installMessageForIos = this.showInstallButton;
          this.installMessageForAndroid = !this.showInstallButton;
        } else {
          if (this.hasDeferredPromptFired && this.currentUrl.includes('/dashboard') && !isIos) {
            setTimeout(() => {
              this.checkIfShowBanner();
              this._cdr.detectChanges();
            }, 0);
          } else {
            // Hide banner on non-dashboard routes
            this.showInstallButton = false;
            this._cdr.detectChanges();
          }
        }
      });

    window.addEventListener('appinstalled', () => {
      this.showInstallButton = false;
      localStorage.setItem('AppInstalled', 'true');
    });

    // When user is authenticated, save email for login_hint and clear stale logout-event
    this._authService.isAuthenticated$.pipe(
      filter(isAuth => isAuth),
      first(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      localStorage.removeItem('logout-event');
      this._authService.user$.pipe(first()).subscribe(user => {
        if (user?.email) {
          localStorage.setItem('lastLoginEmail', user.email);
        }
      });
    });

    this._authService.error$
    .pipe(
      takeUntil(this.destroy$),
      filter(
        (e) =>
          e instanceof GenericError &&
          (e.error === 'login_required' || e.error === 'invalid_grant')
      ),
      exhaustMap(() => {
        if (!this.isLoggingOut) {
          // Auth0 silent refresh often fails when the browser tab is minimized,
          // backgrounded, or when third-party cookies are blocked. Don't do a
          // full logout (which wipes localStorage and triggers cross-tab logout).
          // Just redirect to /login so Auth0 can re-authenticate — session data
          // (MetaData, PharmacyInfo) is preserved so user goes straight to
          // dashboard after re-auth.
          console.log('[AppComponent] Auth error (login_required/invalid_grant) → redirecting to login (preserving session)');
          this.router.navigate(['/login']);
          return EMPTY;
        }
        return EMPTY;
      })
    )
    .subscribe();


  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.storageListener);
    document.removeEventListener('visibilitychange', this.visibilityListener);
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
     this.destroy$.next();
    this.destroy$.complete();
  }

  async logoutAndRedirect() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    // Don't set logout-event here — this method is called as a RECEIVER
    // (from storage listener or error$ handler). The initiating tab's
    // peAuthService.logout() already set it.
    setTimeout(() => {
      const lastEmail = localStorage.getItem('lastLoginEmail');
      localStorage.clear();
      sessionStorage.clear();
      if (lastEmail) {
        localStorage.setItem('lastLoginEmail', lastEmail);
      }
      // Clear logout-event so we don't keep triggering logout on every visibility change
      localStorage.removeItem('logout-event');

      this._authService.logout({
        logoutParams: { returnTo: window.location.origin }
      }).subscribe({
        error: () => {
          this.isLoggingOut = false;
          this.router.navigate(['/login']);
        },
        complete: () => {
          this.isLoggingOut = false;
          this.router.navigate(['/login']);
        }
      });
    }, 100);
  }

  installApp() {
    this.showInstallButton = false;
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((result: any) => {
        if (result.outcome === 'accepted') {
          this.snackBar.open('App installed successfully!', 'Close', {
            duration: 1000, // shown for 1 seconds
          });
        } else {
          this.showInstallButton = false;
        }
        this.deferredPrompt = null;
      });
    }
  }

  dismissBanner() {
    localStorage.setItem('installBannerDismissed', 'true');
    this.showInstallButton = false;
    this._cdr.detectChanges();
  }

  checkIfShowBanner() {
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    const dismissed = localStorage.getItem('installBannerDismissed');
    const isGuestUser = localStorage.getItem('IsGuestUser');

    this.showInstallButton =
      !isInStandaloneMode &&
      this.deferredPrompt &&
      !dismissed &&
      isGuestUser !== 'true' &&
      this.currentUrl?.includes('/dashboard');

    this.installMessageForAndroid = this.showInstallButton;
    this.installMessageForIos = !this.showInstallButton;
  }

  isInstallable(): boolean {
    return !!this.deferredPrompt;
  }
}
